// dashboard-agent-ia/src/pages/ocass/OcassAuth.js
// Authentification Ocass — design moderne avec hero (login + register), OTP minuteur,
// PIN sécurisé, vérification numéro en temps réel. Réutilise PhoneInput/PinInput/LangSelector v1.
import React, { useState, useEffect, useRef } from 'react'
import axios from 'axios'
import { MdStorefront, MdArrowForward, MdWhatsapp, MdCheck, MdArrowBack, MdShoppingBag, MdLocationOn } from 'react-icons/md'
import PhoneInput, { countries } from '../../components/PhoneInput'
import PinInput from '../../components/PinInput'
import LangSelector from '../../components/LangSelector'
import { useLang } from '../../context/LangContext'
import { validatePin } from '../../utils/pinValidation'
import { SECTORS, titleCaseLive } from './utils/sectors'
import { REGIONS, getDepartments } from './utils/senegalGeo'

const API_URL = process.env.REACT_APP_API_URL || 'https://agent-ia-senegal.onrender.com'
const OCASS = `${API_URL}/ocass`
const TOKEN_KEY = 'ocass_merchant_token'
const cleanDigits = (raw) => raw.replace(/\D/g, '')

// ─── Traductions FR / EN (WO retombe sur FR pour l'instant) ───
const TR = {
  fr: {
    heroTitle1: 'Votre boutique', heroTitle2: 'sur WhatsApp', heroTitle3: 'vend pour vous',
    heroDesc: "L'assistant qui présente vos produits, négocie comme au marché, et enregistre les commandes — 24h/24, en wolof et français.",
    f1t: 'Vente automatique', f1d: 'Présente vos produits et négocie les prix',
    f2t: 'WhatsApp natif', f2d: 'Vos clients écrivent sur votre numéro',
    f3t: 'Visible localement', f3d: 'Les clients proches vous trouvent facilement',
    login: 'Connexion', phone: 'Numéro WhatsApp', pinLabel: 'Code PIN à 6 chiffres',
    signin: 'Se connecter', signing: 'Connexion...', forgot: 'PIN oublié ?', register: "S'inscrire",
    createShop: 'Créer ma boutique', stepOf: (n) => `Étape ${n} sur 3`,
    s0: 'Votre commerce', s1: 'Où êtes-vous ?', s2: 'Sécurité du compte',
    shopName: 'Nom de la boutique', yourName: 'Votre nom', sector: "Secteur d'activité", chooseSector: '— Choisissez votre secteur —',
    locHint: 'Pour que les clients proches vous trouvent.', region: 'Région', department: 'Département', choose: '— Choisir —',
    address: 'Adresse précise', whatsappBiz: 'Numéro WhatsApp Business',
    checkingPhone: 'Vérification du numéro...', phoneTaken: '⚠️ Ce numéro est déjà inscrit. Connectez-vous plutôt.',
    phoneFree: '✓ Numéro disponible', otpWarn: '⚠️ Le code de vérification sera envoyé sur ce WhatsApp.',
    choosePin: 'Choisissez votre PIN à 6 chiffres', confirmPin: 'Confirmez votre PIN',
    back: 'Retour', cancel: 'Annuler', create: 'Créer', next: 'Continuer',
    already: 'Déjà inscrit ?', verifyWa: 'Vérification WhatsApp', sentCode: 'Nous avons envoyé un code à 6 chiffres sur',
    verifCode: 'Code de vérification', codeValid: 'Code valable encore', codeExpired: 'Code expiré',
    validate: 'Valider et entrer', verifying: 'Vérification...', resend: 'Renvoyer le code', resendIn: (s) => `Renvoyer dans ${s}s`,
    recovery: 'Récupération PIN', recoverySub: 'Recevez un code sur WhatsApp', sendCode: 'Envoyer le code', sending: 'Envoi...',
    newPinTitle: 'Nouveau PIN', waCode: 'Code WhatsApp', newPin: 'Nouveau PIN', modify: 'Modifier', modifying: 'Modification...',
    errPhonePin: 'Numéro ou PIN incorrect', errNotFound: 'Numéro non trouvé', errPinMismatch: 'Les deux PIN ne correspondent pas',
    errPhoneInvalid: 'Numéro WhatsApp invalide', errPhoneTaken: 'Ce numéro est déjà inscrit. Connectez-vous.',
    errFields0: 'Nom de boutique et secteur requis', errFields1: 'Région et département requis',
    pinModified: 'PIN modifié ! Connectez-vous.', newCodeSent: 'Nouveau code envoyé ✅', notVerified: 'Compte non vérifié. Entrez le code reçu sur WhatsApp.',
    tagline: 'par LeBinaire · Commerce intelligent 🇸🇳',
  },
  en: {
    heroTitle1: 'Your shop', heroTitle2: 'on WhatsApp', heroTitle3: 'sells for you',
    heroDesc: 'The assistant that showcases your products, negotiates like at the market, and records orders — 24/7, in Wolof and French.',
    f1t: 'Automatic selling', f1d: 'Showcases your products and negotiates prices',
    f2t: 'Native WhatsApp', f2d: 'Your clients write on your number',
    f3t: 'Locally visible', f3d: 'Nearby clients find you easily',
    login: 'Sign in', phone: 'WhatsApp Number', pinLabel: '6-digit PIN',
    signin: 'Sign in', signing: 'Signing in...', forgot: 'Forgot PIN?', register: 'Sign up',
    createShop: 'Create my shop', stepOf: (n) => `Step ${n} of 3`,
    s0: 'Your business', s1: 'Where are you?', s2: 'Account security',
    shopName: 'Shop name', yourName: 'Your name', sector: 'Business sector', chooseSector: '— Choose your sector —',
    locHint: 'So nearby clients can find you.', region: 'Region', department: 'Department', choose: '— Choose —',
    address: 'Exact address', whatsappBiz: 'WhatsApp Business number',
    checkingPhone: 'Checking number...', phoneTaken: '⚠️ This number is already registered. Please sign in.',
    phoneFree: '✓ Number available', otpWarn: '⚠️ The verification code will be sent to this WhatsApp.',
    choosePin: 'Choose your 6-digit PIN', confirmPin: 'Confirm your PIN',
    back: 'Back', cancel: 'Cancel', create: 'Create', next: 'Next',
    already: 'Already registered?', verifyWa: 'WhatsApp Verification', sentCode: 'We sent a 6-digit code to',
    verifCode: 'Verification code', codeValid: 'Code valid for', codeExpired: 'Code expired',
    validate: 'Verify and enter', verifying: 'Verifying...', resend: 'Resend code', resendIn: (s) => `Resend in ${s}s`,
    recovery: 'PIN Recovery', recoverySub: 'Receive a code on WhatsApp', sendCode: 'Send code', sending: 'Sending...',
    newPinTitle: 'New PIN', waCode: 'WhatsApp code', newPin: 'New PIN', modify: 'Update', modifying: 'Updating...',
    errPhonePin: 'Invalid number or PIN', errNotFound: 'Number not found', errPinMismatch: 'PINs do not match',
    errPhoneInvalid: 'Invalid WhatsApp number', errPhoneTaken: 'This number is already registered. Please sign in.',
    errFields0: 'Shop name and sector required', errFields1: 'Region and department required',
    pinModified: 'PIN updated! Sign in.', newCodeSent: 'New code sent ✅', notVerified: 'Account not verified. Enter the code received on WhatsApp.',
    tagline: 'by LeBinaire · Smart Commerce 🇸🇳',
  },
}

// ─── Panneau Hero (réutilisé sur login ET register) ───
const Hero = ({ t }) => (
  <div className="lg:w-1/2 flex flex-col justify-center p-10 lg:p-16">
    <div className="flex items-center gap-3 mb-8">
      <div className="w-12 h-12 bg-blue-500 rounded-xl flex items-center justify-center">
        <MdStorefront size={28} className="text-white" />
      </div>
      <div>
        <p className="text-white font-bold text-xl">Marché Ocass</p>
        <p className="text-blue-400 text-sm">{t.tagline}</p>
      </div>
    </div>
    <h1 className="text-4xl lg:text-5xl font-bold text-white leading-tight mb-6">
      {t.heroTitle1}<br /><span className="text-blue-400">{t.heroTitle2}</span><br />{t.heroTitle3}
    </h1>
    <p className="text-slate-300 text-lg mb-10 leading-relaxed">{t.heroDesc}</p>
    <div className="grid grid-cols-1 gap-4 max-w-md">
      {[
        { icon: <MdShoppingBag size={22} />, title: t.f1t, desc: t.f1d },
        { icon: <MdWhatsapp size={22} />, title: t.f2t, desc: t.f2d },
        { icon: <MdLocationOn size={22} />, title: t.f3t, desc: t.f3d },
      ].map((f, i) => (
        <div key={i} className="flex items-center gap-4 bg-white/5 rounded-2xl p-4 border border-white/10">
          <div className="w-10 h-10 bg-blue-500/20 rounded-xl flex items-center justify-center text-blue-400 flex-shrink-0">{f.icon}</div>
          <div>
            <p className="text-white font-semibold text-sm">{f.title}</p>
            <p className="text-slate-400 text-sm">{f.desc}</p>
          </div>
        </div>
      ))}
    </div>
  </div>
)

export default function OcassAuth({ onLogin }) {
  const { lang } = useLang()
  const t = TR[lang] || TR.fr
  const [step, setStep] = useState('login') // login | forgot | reset | register | otp

  // Agent courant (Ocass par défaut — extensible à d'autres agents plus tard)
  const AGENT_SLUG = 'ocass'

  // Secteurs + vocabulaire chargés dynamiquement depuis l'agent (fallback sur l'import statique)
  const [sectors, setSectors] = useState(SECTORS)
  const [vocab, setVocab] = useState({ sectorLabel: "Secteur d'activité", sectorPlaceholder: 'Choisissez votre secteur' })
  useEffect(() => {
    axios.get(`${OCASS}/reference/sectors`, { params: { agent: AGENT_SLUG } })
      .then(r => { if (Array.isArray(r.data) && r.data.length) setSectors(r.data) })
      .catch(() => {})
    axios.get(`${OCASS}/reference/agent`, { params: { slug: AGENT_SLUG } })
      .then(r => { if (r.data?.vocab) setVocab(r.data.vocab) })
      .catch(() => {})
  }, [])
  const [regStep, setRegStep] = useState(0)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')
  const [success, setSuccess] = useState('')

  const [selectedCountry, setSelectedCountry] = useState(countries[0])
  const [rawPhone, setRawPhone] = useState('')
  const [pin, setPin] = useState('')
  const [pinConfirm, setPinConfirm] = useState('')
  const [pinError, setPinError] = useState('')
  const [newPin, setNewPin] = useState('')
  const [otp, setOtp] = useState('')

  const [businessName, setBusinessName] = useState('')
  const [ownerName, setOwnerName] = useState('')
  const [sectorId, setSectorId] = useState('')
  const [region, setRegion] = useState('')
  const [department, setDepartment] = useState('')
  const [address, setAddress] = useState('')

  // Vérification numéro temps réel
  const [phoneExists, setPhoneExists] = useState(false)
  const [checkingPhone, setCheckingPhone] = useState(false)

  // Minuteur OTP
  const [otpTimer, setOtpTimer] = useState(0)
  const timerRef = useRef(null)

  const fullPhone = `${selectedCountry.dial}${cleanDigits(rawPhone)}`
  const phoneOk = cleanDigits(rawPhone).length === selectedCountry.digits

  // ── Minuteur décompte ──
  useEffect(() => {
    if (otpTimer <= 0) return
    timerRef.current = setTimeout(() => setOtpTimer(t => t - 1), 1000)
    return () => clearTimeout(timerRef.current)
  }, [otpTimer])

  const startOtpTimer = () => setOtpTimer(300) // 5 min
  const fmtTimer = () => {
    const m = Math.floor(otpTimer / 60), s = otpTimer % 60
    return `${m}:${String(s).padStart(2, '0')}`
  }

  // ── Vérif numéro en temps réel (register, étape PIN) ──
  useEffect(() => {
    if (step !== 'register' || regStep !== 2 || !phoneOk) { setPhoneExists(false); return }
    setCheckingPhone(true)
    const t = setTimeout(async () => {
      try {
        const res = await axios.get(`${OCASS}/merchant/check-phone`, { params: { phone: fullPhone } })
        setPhoneExists(res.data.exists)
      } catch { setPhoneExists(false) }
      finally { setCheckingPhone(false) }
    }, 500)
    return () => clearTimeout(t)
  }, [fullPhone, phoneOk, step, regStep])

  const handlePhoneChange = (e) => {
    const val = e.target.value.replace(/[^\d\s]/g, '')
    if (cleanDigits(val).length > selectedCountry.digits) return
    setRawPhone(val)
  }

  // PIN sécurisé (register)
  const handlePinChange = (val) => {
    setPin(val)
    if (val.length === 6) setPinError(validatePin(val, cleanDigits(rawPhone), 'fr') || '')
    else setPinError('')
  }

  // ── LOGIN ──
  const handleLogin = async (e) => {
    if (e) e.preventDefault()
    if (pin.length !== 6) return
    setLoading(true); setError('')

    // Login universel : le backend identifie l'agent et renvoie où rediriger
    try {
      const res = await axios.post(`${OCASS}/auth/login`, { phone: fullPhone, pin })
      const { kind, token, redirect } = res.data

      if (kind === 'admin') {
        localStorage.setItem('ocass_admin_token', token)
        window.location.href = redirect || '/ocass/admin'
        return
      }
      if (kind === 'ocass_merchant') {
        localStorage.setItem(TOKEN_KEY, token)
        // Si on est déjà dans le MerchantDashboard (porte /ocass), on connecte en place
        if (onLogin) { onLogin(token, res.data.profile); return }
        window.location.href = redirect || '/ocass'
        return
      }
      // Futurs agents : rediriger selon redirect
      if (redirect) { localStorage.setItem('ocass_merchant_token', token); window.location.href = redirect; return }
    } catch (err) {
      if (err.response?.data?.needsOtp) { setStep('otp'); startOtpTimer(); setError(t.notVerified) }
      else setError(err.response?.data?.error || t.errPhonePin)
    } finally { setLoading(false) }
  }

  // ── FORGOT / RESET ──
  const handleForgot = async () => {
    setLoading(true); setError('')
    try {
      await axios.post(`${OCASS}/merchant/forgot-pin`, { phone: fullPhone })
      setStep('reset'); startOtpTimer()
    } catch (err) { setError(err.response?.data?.error || t.errNotFound) }
    finally { setLoading(false) }
  }
  const handleReset = async () => {
    if (otp.length !== 6 || newPin.length !== 6) return
    const pErr = validatePin(newPin, cleanDigits(rawPhone), 'fr')
    if (pErr) { setError(pErr); return }
    setLoading(true); setError('')
    try {
      await axios.post(`${OCASS}/merchant/reset-pin`, { phone: fullPhone, otp, newPin })
      setSuccess(t.pinModified); setStep('login'); setPin(''); setOtp(''); setNewPin('')
    } catch (err) { setError(err.response?.data?.error || t.errPhonePin) }
    finally { setLoading(false) }
  }

  // ── REGISTER ──
  const nextReg = () => {
    setError('')
    if (regStep === 0 && (!businessName || !sectorId)) { setError(t.errFields0); return }
    if (regStep === 1 && (!region || !department)) { setError(t.errFields1); return }
    if (regStep === 2) {
      if (!phoneOk) { setError(t.errPhoneInvalid); return }
      if (phoneExists) { setError(t.errPhoneTaken); return }
      const pErr = validatePin(pin, cleanDigits(rawPhone), 'fr')
      if (pErr) { setPinError(pErr); return }
      if (pin !== pinConfirm) { setError(t.errPinMismatch); return }
      submitRegister(); return
    }
    setRegStep(s => s + 1)
  }
  const submitRegister = async () => {
    setLoading(true); setError('')
    try {
      await axios.post(`${OCASS}/merchant/register`, { businessName, ownerName, phone: fullPhone, pin, sectorId, region, department, address })
      setStep('otp'); startOtpTimer()
    } catch (err) { setError(err.response?.data?.error || "Erreur lors de l'inscription") }
    finally { setLoading(false) }
  }
  const verifyOtp = async () => {
    if (otp.length !== 6) return
    setLoading(true); setError('')
    try {
      const res = await axios.post(`${OCASS}/merchant/verify-otp`, { phone: fullPhone, code: otp })
      localStorage.setItem(TOKEN_KEY, res.data.token)
      if (onLogin) onLogin(res.data.token, res.data.merchant)
      else window.location.href = '/ocass'
    } catch (err) { setError(err.response?.data?.error || t.errPhonePin) }
    finally { setLoading(false) }
  }
  const resendOtp = async () => {
    setError('')
    try { await axios.post(`${OCASS}/merchant/resend-otp`, { phone: fullPhone }); setOtp(''); startOtpTimer(); setSuccess(t.newCodeSent); setTimeout(() => setSuccess(''), 3000) }
    catch (err) { setError(err.response?.data?.error || 'Erreur') }
  }

  const regProgressEl = (
    <div className="flex gap-2 mb-6">
      {[0, 1, 2].map(i => <div key={i} className={`h-1 flex-1 rounded-full transition-all ${i <= regStep ? 'bg-blue-600' : 'bg-gray-200'}`} />)}
    </div>
  )
  const inputCls = "w-full px-4 py-3.5 border-2 border-gray-200 rounded-xl focus:outline-none focus:border-blue-500 text-gray-800 transition-colors"
  const selectCls = inputCls + " bg-white"
  const labelCls = "block text-sm font-medium text-gray-700 mb-2"
  // Classes wrapper carte (utilisées en JSX inline, PAS comme composant — sinon perte de focus)
  const cardOuter = "lg:w-1/2 flex items-center justify-center p-6 lg:p-12"
  const cardInner = "bg-white rounded-3xl shadow-2xl w-full max-w-md p-6 sm:p-8"

  // ════════════ REGISTER (hero + carte étapes) ════════════
  if (step === 'register') {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row" style={{ background: 'linear-gradient(135deg,#0f172a,#1e1b4b,#0f172a)' }}>
        <Hero t={t} />
        <div className={cardOuter}><div className={cardInner}>
          <div className="flex justify-center mb-5"><LangSelector center dark={false} /></div>
          {regProgressEl}
          <p className="text-sm text-blue-600 font-medium mb-1">{t.stepOf(regStep + 1)}</p>
          <h2 className="text-xl font-bold text-gray-800 mb-5">
            {regStep === 0 ? t.s0 : regStep === 1 ? t.s1 : t.s2}
          </h2>
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm text-center">{error}</div>}

          {regStep === 0 && (
            <div className="space-y-4">
              <div><label className={labelCls}>{t.shopName}</label>
                <input value={businessName} onChange={e => setBusinessName(titleCaseLive(e.target.value))} placeholder="Quincaillerie Serigne Fallou" className={inputCls} /></div>
              <div><label className={labelCls}>{t.yourName}</label>
                <input value={ownerName} onChange={e => setOwnerName(titleCaseLive(e.target.value))} placeholder="Modou Diop" className={inputCls} /></div>
              <div><label className={labelCls}>{vocab.sectorLabel || t.sector}</label>
                <select value={sectorId} onChange={e => setSectorId(e.target.value)} className={selectCls}>
                  <option value="">— {vocab.sectorPlaceholder || 'Choisir'} —</option>
                  {sectors.map(s => <option key={s.id} value={s.id}>{s.label}</option>)}
                </select>
                {sectorId && <p className="text-xs text-gray-400 mt-1.5">{sectors.find(s => s.id === sectorId)?.hint}</p>}
              </div>
            </div>
          )}

          {regStep === 1 && (
            <div className="space-y-4">
              <p className="text-gray-500 text-sm -mt-2">{t.locHint}</p>
              <div><label className={labelCls}>{t.region}</label>
                <select value={region} onChange={e => { setRegion(e.target.value); setDepartment('') }} className={selectCls}>
                  <option value="">— Choisir —</option>{REGIONS.map(r => <option key={r} value={r}>{r}</option>)}
                </select></div>
              <div><label className={labelCls}>{t.department}</label>
                <select value={department} onChange={e => setDepartment(e.target.value)} disabled={!region} className={selectCls} style={{ opacity: region ? 1 : 0.5 }}>
                  <option value="">— Choisir —</option>{getDepartments(region).map(d => <option key={d} value={d}>{d}</option>)}
                </select></div>
              <div><label className={labelCls}>{t.address}</label>
                <input value={address} onChange={e => setAddress(titleCaseLive(e.target.value))} placeholder="Marché Tilène, étal 42" className={inputCls} /></div>
            </div>
          )}

          {regStep === 2 && (
            <div className="space-y-5">
              <div>
                <label className={labelCls}>{t.whatsappBiz}</label>
                <PhoneInput value={rawPhone} onChange={handlePhoneChange}
                  selectedCountry={selectedCountry} setSelectedCountry={setSelectedCountry}
                  onCountryChange={() => setRawPhone('')} error="" phonePreviewLabel="Numéro" checkExists={false} />
                {checkingPhone && <p className="text-xs text-gray-400 mt-2">{t.checkingPhone}</p>}
                {phoneExists && <p className="text-xs text-red-500 mt-2">{t.phoneTaken}</p>}
                {!phoneExists && !checkingPhone && phoneOk && <p className="text-xs text-green-600 mt-2">{t.phoneFree}</p>}
                {!phoneExists && <p className="text-xs text-amber-600 mt-1">{t.otpWarn}</p>}
              </div>
              <PinInput value={pin} onChange={handlePinChange} label={t.choosePin} id="reg-pin" error={pinError} />
              <PinInput value={pinConfirm} onChange={setPinConfirm} label={t.confirmPin} id="reg-pinc" />
            </div>
          )}

          <div className="flex gap-3 mt-6">
            {regStep > 0 ? (
              <button onClick={() => { setRegStep(s => s - 1); setError('') }} className="flex-1 py-3.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50 flex items-center justify-center gap-1"><MdArrowBack size={18} /> {t.back}</button>
            ) : (
              <button onClick={() => { setStep('login'); setError('') }} className="flex-1 py-3.5 border border-gray-200 text-gray-600 rounded-xl hover:bg-gray-50">{t.cancel}</button>
            )}
            <button onClick={nextReg} disabled={loading || (regStep === 2 && (phoneExists || !!pinError))}
              className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2">
              {loading ? '...' : regStep === 2 ? <>{t.create} <MdCheck size={20} /></> : <>{t.next} <MdArrowForward size={20} /></>}
            </button>
          </div>
          <p className="text-center text-gray-400 text-sm mt-5">{t.already} <button onClick={() => { setStep('login'); setError('') }} className="text-blue-600 hover:underline font-medium">{t.signin}</button></p>
        </div></div>
      </div>
    )
  }

  // ════════════ OTP (hero + carte) ════════════
  if (step === 'otp') {
    return (
      <div className="min-h-screen flex flex-col lg:flex-row" style={{ background: 'linear-gradient(135deg,#0f172a,#1e1b4b,#0f172a)' }}>
        <Hero t={t} />
        <div className={cardOuter}><div className={cardInner}>
          <div className="flex justify-center mb-3"><MdWhatsapp size={46} className="text-green-500" /></div>
          <h2 className="text-xl font-bold text-gray-800 mb-1 text-center">{t.verifyWa}</h2>
          <p className="text-gray-500 text-sm mb-2 text-center">{t.sentCode}</p>
          <p className="text-blue-600 font-semibold text-center mb-4">{fullPhone}</p>
          {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm text-center">{error}</div>}
          {success && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm text-center">{success}</div>}
          <PinInput value={otp} onChange={setOtp} label={t.verifCode} id="otp-verify" autoFocus />
          <div className="text-center mt-3">
            {otpTimer > 0
              ? <p className="text-sm text-gray-400">{t.codeValid} <b className="text-gray-700">{fmtTimer()}</b></p>
              : <p className="text-sm text-red-500">{t.codeExpired}</p>}
          </div>
          <button onClick={verifyOtp} disabled={loading || otp.length !== 6}
            className="w-full mt-4 bg-green-600 hover:bg-green-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2">
            {loading ? t.verifying : <><MdCheck size={20} /> {t.validate}</>}
          </button>
          <button onClick={resendOtp} disabled={otpTimer > 240} className="w-full mt-3 text-gray-400 hover:text-gray-600 disabled:opacity-40 text-sm py-2">
            {otpTimer > 240 ? t.resendIn(otpTimer - 240) : t.resend}
          </button>
        </div></div>
      </div>
    )
  }

  // ════════════ LOGIN / FORGOT / RESET (hero + carte) ════════════
  return (
    <div className="min-h-screen flex flex-col lg:flex-row" style={{ background: 'linear-gradient(135deg,#0f172a,#1e1b4b,#0f172a)' }}>
      <Hero t={t} />
      <div className={cardOuter}><div className={cardInner}>
        <div className="flex justify-center mb-6"><LangSelector center dark={false} /></div>
        {error && <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm text-center">{error}</div>}
        {success && <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-xl text-green-600 text-sm text-center">{success}</div>}

        {step === 'login' && (
          <form onSubmit={handleLogin}>
            <h2 className="text-xl font-bold text-gray-800 mb-5 text-center">{t.login}</h2>
            <div className="mb-5">
              <label className={labelCls}>{t.phone}</label>
              <PhoneInput value={rawPhone} onChange={handlePhoneChange}
                selectedCountry={selectedCountry} setSelectedCountry={setSelectedCountry}
                onCountryChange={() => setRawPhone('')} error="" phonePreviewLabel="Numéro" checkExists={false} />
            </div>
            <div className="mb-6"><PinInput value={pin} onChange={setPin} label={t.pinLabel} id="login-pin" /></div>
            <button type="submit" disabled={loading || pin.length !== 6}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl flex items-center justify-center gap-2">
              {loading ? t.signing : <><span>{t.signin}</span><MdArrowForward size={20} /></>}
            </button>
            <div className="flex items-center justify-between mt-4 text-sm">
              <button type="button" onClick={() => { setStep('forgot'); setError('') }} className="text-blue-600 hover:underline">{t.forgot}</button>
              <button type="button" onClick={() => { setStep('register'); setRegStep(0); setError('') }} className="text-gray-500 hover:text-blue-600 font-medium">{t.register}</button>
            </div>
          </form>
        )}

        {step === 'forgot' && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-1 text-center">{t.recovery}</h2>
            <p className="text-gray-500 text-sm mb-4 text-center">{t.recoverySub}</p>
            <div className="mb-5">
              <PhoneInput value={rawPhone} onChange={handlePhoneChange}
                selectedCountry={selectedCountry} setSelectedCountry={setSelectedCountry}
                onCountryChange={() => setRawPhone('')} error="" phonePreviewLabel="Numéro" checkExists={false} />
            </div>
            <button onClick={handleForgot} disabled={loading || !phoneOk}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl">
              {loading ? t.sending : t.sendCode}
            </button>
            <button onClick={() => { setStep('login'); setError('') }} className="w-full mt-3 text-gray-400 text-sm py-2">← {t.back}</button>
          </div>
        )}

        {step === 'reset' && (
          <div>
            <h2 className="text-lg font-bold text-gray-800 mb-4 text-center">{t.newPinTitle}</h2>
            <div className="mb-2"><PinInput value={otp} onChange={setOtp} label={t.waCode} id="otp-r" autoFocus /></div>
            <div className="text-center mb-4">{otpTimer > 0 ? <p className="text-xs text-gray-400">Valable {fmtTimer()}</p> : <p className="text-xs text-red-500">Code expiré</p>}</div>
            <div className="mb-5"><PinInput value={newPin} onChange={setNewPin} label={t.newPin} id="newpin-r" /></div>
            <button onClick={handleReset} disabled={loading || otp.length !== 6 || newPin.length !== 6}
              className="w-full bg-blue-600 hover:bg-blue-700 disabled:opacity-50 text-white font-semibold py-3.5 rounded-xl">
              {loading ? t.modifying : t.modify}
            </button>
            <button onClick={handleForgot} className="w-full mt-3 text-blue-500 text-sm py-2">Renvoyer</button>
          </div>
        )}
      </div></div>
    </div>
  )
}
